import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "../../utils/axiosInstance";
import { UserContext } from "../../utils/userContext";
import {
  canManageOrganisationUsers,
  formatOrganisationRole,
  ORGANISATION_ROLES,
} from "../../utils/roles";
import PageHeader from "../../components/ui/PageHeader";
import PageWrapper from "../../components/ui/PageWrapper";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import StatusBadge from "../../components/ui/StatusBadge";
import AdminBreadcrumb from "../../components/admin/AdminBreadcrumb";
import { useBackNavigation } from "../../navigation/BackNavigationContext";
import { AVAILABLE_UI_LANGUAGES } from "../../constants/languages";

export default function UserDetailPage() {
  const { customerId, organisationId, userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useContext(UserContext);
  const { setPageFallback, markAsRoot, excludeFromHistory } = useBackNavigation();

  const basePath = `/admin/customers/${customerId}/organisations/${organisationId}/users/${userId}`;
  const isEditPath = location.pathname.endsWith("/edit");
  const canEdit = canManageOrganisationUsers(currentUser);

  useEffect(() => {
    if (isEditPath) {
      setPageFallback(basePath);
      markAsRoot(false);
      excludeFromHistory(true);
    } else {
      setPageFallback(null);
      markAsRoot(false);
      excludeFromHistory(false);
    }
  }, [isEditPath, basePath, setPageFallback, markAsRoot, excludeFromHistory]);

  const [userDetails, setUserDetails] = useState(null);
  const [orgName, setOrgName] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    firstname: "",
    lastname: "",
    email: "",
    companyRole: ORGANISATION_ROLES.USER,
    uiLanguage: "",
    translateFrom: [],
    translateTo: [],
    hourlyCost: "",
    hourlyRate: "",
  });

  const fetchUser = async () => {
    setLoading(true);
    setError(null);
    try {
      const [userRes, orgRes] = await Promise.all([
        axios.get(`/users/${userId}`, { params: { organisationId } }),
        axios.get(`/admin/organisations/${organisationId}`),
      ]);
      setUserDetails(userRes.data);
      setOrgName(orgRes.data?.organisation?.name || orgRes.data?.company?.name || "");
      setForm({
        firstname: userRes.data.firstname || "",
        lastname: userRes.data.lastname || "",
        email: userRes.data.email || "",
        companyRole: userRes.data.companyRole || ORGANISATION_ROLES.USER,
        uiLanguage: userRes.data.languages?.ui || "",
        translateFrom: userRes.data.languages?.translateFrom || [],
        translateTo: userRes.data.languages?.translateTo || [],
        hourlyCost:
          userRes.data.hourlyCost !== null && userRes.data.hourlyCost !== undefined
            ? userRes.data.hourlyCost
            : "",
        hourlyRate:
          userRes.data.hourlyRate !== null && userRes.data.hourlyRate !== undefined
            ? userRes.data.hourlyRate
            : "",
      });
    } catch (err) {
      setError("Failed to load user");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, organisationId, location.pathname]);

  const onSave = async (e) => {
    e?.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    try {
      const payload = {
        firstname: form.firstname,
        lastname: form.lastname,
        email: form.email,
        companyRole: form.companyRole,
        uiLanguage: form.uiLanguage,
        translateFrom: form.translateFrom,
        translateTo: form.translateTo,
      };
      if (form.hourlyCost !== "") payload.hourlyCost = parseFloat(form.hourlyCost) || null;
      if (form.hourlyRate !== "") payload.hourlyRate = parseFloat(form.hourlyRate) || null;

      await axios.put(`/users/${userId}`, payload, { params: { organisationId } });
      await fetchUser();
      navigate(basePath);
    } catch (err) {
      setError(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onToggleArchive = async () => {
    if (!canEdit) return;
    const action = userDetails.isArchived ? "unarchive" : "archive";
    if (!confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} this user?`)) return;
    try {
      if (userDetails.isArchived) {
        await axios.put(`/users/${userId}`, { isArchived: false }, { params: { organisationId } });
      } else {
        await axios.delete(`/users/${userId}`, { params: { organisationId } });
      }
      await fetchUser();
    } catch {
      setError("Failed to update status");
    }
  };

  const onResetPassword = async () => {
    if (!canEdit) return;
    const newPassword = prompt("Enter new password (min 6 characters):");
    if (!newPassword || newPassword.length < 6) return;
    try {
      await axios.post(`/users/${userId}/set-password`, { password: newPassword });
      alert("Password reset successfully.");
    } catch {
      setError("Failed to reset password");
    }
  };

  if (loading) return <PageWrapper><p>Loading…</p></PageWrapper>;
  if (error && !userDetails) return <PageWrapper><p className="text-red-600">{error}</p></PageWrapper>;

  return (
    <>
      <PageHeader
        title={userDetails ? `${userDetails.firstname} ${userDetails.lastname}` : "User"}
        breadcrumb={
          <AdminBreadcrumb
            items={[
              { label: orgName || "Department", to: `/admin/customers/${customerId}/organisations/${organisationId}` },
              { label: userDetails ? `${userDetails.firstname} ${userDetails.lastname}` : "User" },
            ]}
          />
        }
        actions={
          canEdit && !isEditPath ? (
            <>
              <Button onClick={() => navigate(`${basePath}/edit`)} variant="outline" size="md" className="mr-2">
                Edit
              </Button>
              <Button
                onClick={onToggleArchive}
                variant={userDetails?.isArchived ? "solid" : "danger"}
                size="md"
              >
                {userDetails?.isArchived ? "Unarchive" : "Archive"}
              </Button>
            </>
          ) : null
        }
      />
      <PageWrapper>
        {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}

        {isEditPath && canEdit ? (
          <form onSubmit={onSave} className="w-full max-w-xl space-y-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block">First Name</label>
                <input
                  className="w-full px-3 py-2 border rounded"
                  value={form.firstname}
                  onChange={(e) => setForm({ ...form, firstname: e.target.value })}
                  required
                />
              </div>
              <div className="flex-1">
                <label className="block">Last Name</label>
                <input
                  className="w-full px-3 py-2 border rounded"
                  value={form.lastname}
                  onChange={(e) => setForm({ ...form, lastname: e.target.value })}
                  required
                />
              </div>
            </div>
            <div>
              <label className="block">Email</label>
              <input
                type="email"
                className="w-full px-3 py-2 border rounded"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block">Department Role</label>
              <select
                className="w-full px-3 py-2 border rounded"
                value={form.companyRole}
                onChange={(e) => setForm({ ...form, companyRole: e.target.value })}
              >
                <option value={ORGANISATION_ROLES.USER}>User</option>
                <option value={ORGANISATION_ROLES.ORGANISATION_ADMIN}>Department Admin</option>
              </select>
            </div>
            <div>
              <label className="block">UI Language</label>
              <select
                className="w-full px-3 py-2 border rounded"
                value={form.uiLanguage}
                onChange={(e) => setForm({ ...form, uiLanguage: e.target.value })}
              >
                {AVAILABLE_UI_LANGUAGES.map((lang) => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block">Hourly Cost (SEK)</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full px-3 py-2 border rounded"
                  value={form.hourlyCost}
                  onChange={(e) => setForm({ ...form, hourlyCost: e.target.value })}
                />
              </div>
              <div className="flex-1">
                <label className="block">Hourly Rate (SEK)</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full px-3 py-2 border rounded"
                  value={form.hourlyRate}
                  onChange={(e) => setForm({ ...form, hourlyRate: e.target.value })}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
              <Button type="button" variant="outline" onClick={() => navigate(basePath)}>Cancel</Button>
            </div>
          </form>
        ) : (
          <Card className="p-6 max-w-2xl">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-2xl font-bold">
                  {userDetails?.firstname} {userDetails?.lastname}
                </h2>
                <p className="text-gray-600">{userDetails?.email}</p>
              </div>
              <StatusBadge status={userDetails?.isArchived ? "archived" : "active"} />
            </div>
            <div className="space-y-2 text-sm">
              <p><span className="font-medium">Role:</span> {formatOrganisationRole(userDetails?.companyRole)}</p>
              {userDetails?.hourlyCost != null && (
                <p><span className="font-medium">Hourly cost:</span> {userDetails.hourlyCost} SEK</p>
              )}
              {userDetails?.hourlyRate != null && (
                <p><span className="font-medium">Hourly rate:</span> {userDetails.hourlyRate} SEK</p>
              )}
            </div>
            {canEdit && (
              <div className="mt-4 pt-4 border-t">
                <Button onClick={onResetPassword} variant="outline" size="md">Reset Password</Button>
              </div>
            )}
          </Card>
        )}
      </PageWrapper>
    </>
  );
}
